import { useOutletContext } from "react-router";
import type { SchrijvenVariantContext } from "@a2/core";
import type { SessionUser } from "../../lib/session-loader";
import { useT } from "../../lib/use-t";

interface BriefjeShellProps {
  variant: SchrijvenVariantContext;
  value: string;
  onChange: (text: string) => void;
  disabled?: boolean;
}

/**
 * Briefje composer — a compact note. Sits on `--panel-soft` (lighter than
 * email/wijkkrant which sit on `--panel`) and is constrained to a narrower
 * column so it reads as a casual handwritten note left on a counter.
 * Greeting and closing are locked; the student writes the middle. Word count
 * lives in the runner's bottom-bar meter.
 */
export function BriefjeShell({
  variant,
  value,
  onChange,
  disabled = false,
}: BriefjeShellProps) {
  const session = useOutletContext<SessionUser>();
  const t = useT();
  const displayName = session?.displayName?.trim() || "Ik";

  return (
    <article className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--panel-soft)] shadow-[var(--shadow)] px-5 py-4 max-w-md mx-auto space-y-3">
      <div className="flex items-center gap-3">
        <p className="font-mono-badge text-[10px] uppercase tracking-wider text-[var(--ink-faint)]">
          Briefje
          {variant.recipient ? ` · voor ${variant.recipient}` : null}
        </p>
        <span className="ml-auto inline-flex items-center gap-1.5 font-mono-badge text-xs text-[var(--ink-faint)]">
          <span className="inline-block w-[7px] h-[7px] rounded-full bg-[var(--pass)]" aria-hidden />
          {t("schrijven.runner.saved")}
        </span>
      </div>

      {variant.greeting ? (
        <p className="text-sm text-[var(--ink)]">{variant.greeting}</p>
      ) : null}

      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        rows={7}
        placeholder={t("schrijven.runner.briefjePlaceholder")}
        className="w-full rounded-md bg-[var(--bg)] border border-[var(--border)] px-3 py-2.5 text-sm text-[var(--ink)] leading-relaxed placeholder:text-[var(--ink-faint)] resize-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2 disabled:opacity-50"
        aria-label={t("schrijven.runner.briefjeLabel")}
      />

      {variant.closing ? (
        <div className="text-sm text-[var(--ink)] leading-relaxed">
          <p>{variant.closing}</p>
          <p>{displayName}</p>
        </div>
      ) : null}
    </article>
  );
}
